const CLOUDFRONT_HOST = 'dupwq54o68dyw.cloudfront.net';

// gloss
export const UPLOAD_CLOUDFRONT_HOSTS = [CLOUDFRONT_HOST, 'test.cloudfront.net'] as const;

// gloss
export const UPLOAD_S3_HOSTS = [
  'zealot-fan-uploads.s3.amazonaws.com',
  'zealot-fan-uploads.s3.us-west-2.amazonaws.com',
  'zealot-fan-uploads.s3.us-east-1.amazonaws.com',
] as const;

// gloss
export const isUploadIframeSrc = (url: string | null | undefined): boolean => {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:') return false;
    const host = parsed.hostname.toLowerCase();
    const hostAllowed =
      (UPLOAD_CLOUDFRONT_HOSTS as readonly string[]).includes(host) ||
      (UPLOAD_S3_HOSTS as readonly string[]).includes(host);
    if (!hostAllowed) return false;
    // why: the iframe allowlist must also require a .pdf path — host-only checks let arbitrary
    // why: HTML through the permissive PDF sandbox (jose #1496).
    return /\.pdf$/i.test(parsed.pathname);
  } catch {
    return false;
  }
};

// gloss
const EMBEDDABLE_HOSTS: Array<{ host: string; pathPrefix?: string }> = [
  { host: 'youtube.com', pathPrefix: '/embed/' },
  { host: 'www.youtube.com', pathPrefix: '/embed/' },
  { host: 'player.vimeo.com' },
  { host: 'www.loom.com', pathPrefix: '/embed/' },
  { host: 'open.spotify.com', pathPrefix: '/embed' },
  { host: 'w.soundcloud.com', pathPrefix: '/player' },
  { host: 'www.instagram.com' },
  { host: 'instagram.com' },
  { host: 'www.tiktok.com', pathPrefix: '/embed/' },
  { host: 'tiktok.com', pathPrefix: '/embed/' },
  { host: 'twitframe.com' },
  { host: 'cpaas.bravais.com', pathPrefix: '/s/' },
  ...UPLOAD_CLOUDFRONT_HOSTS.map((host) => ({ host })),
];

// gloss
export const isCloudFrontVideo = (url: string | null | undefined): boolean => {
  if (!url) return false;
  try {
    return (UPLOAD_CLOUDFRONT_HOSTS as readonly string[]).includes(new URL(url).hostname.toLowerCase());
  } catch {
    return false;
  }
};

// gloss
export const isUploadedMediaUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    // why: match against the exact known CloudFront hostnames, never a `*.cloudfront.net`
    // why: suffix — that also accepts an attacker-owned distribution.
    return (
      parsed.protocol === 'https:' &&
      (UPLOAD_CLOUDFRONT_HOSTS as readonly string[]).includes(parsed.hostname.toLowerCase())
    );
  } catch {
    return false;
  }
};

// gloss
export const isEmbeddableUrl = (url: string): boolean => {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname.toLowerCase();
    return EMBEDDABLE_HOSTS.some(
      ({ host, pathPrefix }) => hostname === host && (!pathPrefix || parsed.pathname.startsWith(pathPrefix)),
    );
  } catch {
    return false;
  }
};

const isHostMatch = (hostname: string, hosts: readonly string[]): boolean => hosts.includes(hostname);

const YOUTUBE_LONG_HOSTS = ['youtube.com', 'www.youtube.com', 'm.youtube.com'] as const;
const YOUTUBE_SHORT_HOSTS = ['youtu.be', 'www.youtu.be'] as const;
const VIMEO_HOSTS = ['vimeo.com', 'www.vimeo.com'] as const;
const LOOM_HOSTS = ['loom.com', 'www.loom.com'] as const;
const SPOTIFY_HOSTS = ['open.spotify.com'] as const;
const SOUNDCLOUD_HOSTS = ['soundcloud.com', 'www.soundcloud.com'] as const;
const INSTAGRAM_HOSTS = ['instagram.com', 'www.instagram.com'] as const;
const TIKTOK_HOSTS = ['tiktok.com', 'www.tiktok.com', 'm.tiktok.com'] as const;
const TWITTER_HOSTS = ['twitter.com', 'www.twitter.com', 'x.com', 'www.x.com'] as const;

// gloss
export const normalizeEmbedUrl = (rawUrl: string): string | null => {
  const url = rawUrl?.trim();
  if (!url) return null;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  const hostname = parsed.hostname.toLowerCase();
  const pathname = parsed.pathname;

  if (isHostMatch(hostname, YOUTUBE_SHORT_HOSTS)) {
    const videoId = pathname.split('/').filter(Boolean)[0];
    if (videoId) return `https://www.youtube.com/embed/${videoId}`;
  }
  if (isHostMatch(hostname, YOUTUBE_LONG_HOSTS)) {
    let videoId: string | null = null;
    if (pathname === '/watch') {
      videoId = parsed.searchParams.get('v');
    } else {
      const match = pathname.match(/^\/(shorts|live|embed)\/([^/?#]+)/);
      if (match) videoId = match[2] ?? null;
    }
    if (videoId) return `https://www.youtube.com/embed/${videoId}`;
  }

  if (isHostMatch(hostname, VIMEO_HOSTS)) {
    const segments = pathname.split('/').filter(Boolean);
    const videoId = segments[segments.length - 1];
    if (videoId) return `https://player.vimeo.com/video/${videoId}`;
  }

  if (isHostMatch(hostname, LOOM_HOSTS) && pathname.startsWith('/share/')) {
    const segments = pathname.split('/').filter(Boolean);
    const videoId = segments[segments.length - 1];
    if (videoId) return `https://www.loom.com/embed/${videoId}`;
  }

  if (isHostMatch(hostname, SPOTIFY_HOSTS)) {
    return `https://open.spotify.com/embed${pathname}`;
  }

  if (isHostMatch(hostname, SOUNDCLOUD_HOSTS)) {
    return `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}`;
  }

  if (isHostMatch(hostname, INSTAGRAM_HOSTS)) {
    const segments = pathname.split('/').filter(Boolean);
    const type = segments[0];
    const id = segments[1];
    if (type && id) return `https://www.instagram.com/${type}/${id}/embed`;
  }

  if (isHostMatch(hostname, TIKTOK_HOSTS)) {
    const match = pathname.match(/\/video\/(\d+)/);
    if (match?.[1]) return `https://www.tiktok.com/embed/v2/${match[1]}`;
  }

  if (isHostMatch(hostname, TWITTER_HOSTS)) {
    return `https://twitframe.com/show?url=${encodeURIComponent(url)}`;
  }

  if (parsed.protocol !== 'https:') return null;

  if (/\.pdf($|[?#])/i.test(url)) {
    const sep = url.includes('#') ? '&' : '#';
    return `${url}${sep}view=FitH`;
  }

  return url;
};

// gloss
export const isSafeUrl = (urlString: string): boolean => {
  try {
    const parsed = new URL(urlString);

    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return false;
    }

    const hostname = parsed.hostname.toLowerCase();

    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1' || hostname === '0.0.0.0') {
      return false;
    }

    const parts = hostname.split('.').map(Number);
    if (parts.length === 4 && parts.every((p) => !isNaN(p))) {
      const [first = -1, second = -1] = parts;
      if (first === 10) return false;
      if (first === 172 && second >= 16 && second <= 31) return false;
      if (first === 192 && second === 168) return false;
      if (first === 169 && second === 254) return false;
      if (first === 0) return false;
    }

    if (hostname === 'metadata.google.internal') return false;

    return true;
  } catch {
    return false;
  }
};
